import { Button, HSpacer, Modal, Select, Text, VSpacer } from '@/components/DesignSystem';
import { useDefaultRetailerId } from '@/components/shared/DefaultRetailerSelector';
import { AppConfig } from "@/constants/AppConfig";
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { TwilioConfigApi } from "@/utilities/api/TwilioConfigApi";
import { MenuItem, Stack } from '@mui/material';
import {
  ScheduledNotificationCommunicationType as ScheduledNotificationType,
} from '@shared/enums/ScheduledNotificationType';
import { SharedConfig } from '@shared/SharedConfig';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { NotificationDetailModal } from './NotificationDetailModal';
import {
  useNotificationCommunicationTypeLabel,
} from '../utils/useNotificationCommunicationTypeLabel';

interface CreateNotificationModalProps {
  open: boolean,
  onClose: () => void,
  onDraftSaved?: () => void,
  testID?: string,
}

export const CreateNotificationModal: React.FC<CreateNotificationModalProps> = ({
  open,
  onClose,
  onDraftSaved,
  testID = 'create-notification-modal',
}) => {
  const { t } = useTranslation();
  const communicationTypeLabel = useNotificationCommunicationTypeLabel();
  const { isInternal, retailer } = useAuthentication();
  const { defaultRetailerId } = useDefaultRetailerId();
  const [retailerId, setRetailerId] = useState(
    isInternal ? defaultRetailerId ?? '' : retailer?.id || '',
  );
  const [communicationType, setCommunicationType] = useState<ScheduledNotificationType | ''>('');
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data: retailers, isFetching: isFetchingRetailers } = useQuery(
    [QueryKeys.GET_RETAILERS],
    () => RetailerApi.list({
      limit: SharedConfig.maxPageLimit,
    }),
    {
      enabled: isInternal,
    },
  );

  const selectedRetailer = retailers?.data?.find(retailer => retailer.id === retailerId);

  const { data: hasExistingTwilioConfig } = useQuery(
    [QueryKeys.GET_RETAILER_TWILIO_CONFIG, isInternal ? selectedRetailer?.id : retailerId],
    async () => TwilioConfigApi.exists(
      isInternal ? selectedRetailer!.id : retailerId,
    ),
    { enabled: isInternal ? !!selectedRetailer : !!retailer },
  );

  const cannotCreateScheduledNotification =
    (!isInternal
      && (AppConfig.env.staging || AppConfig.env.prod || AppConfig.env.uat)
      && !hasExistingTwilioConfig
    ) ||
    (isInternal
      && !!selectedRetailer
      && !hasExistingTwilioConfig
      && !selectedRetailer.messagingTelephone
    );

  return (
    <>
      <Modal
        containerStyleOverride={{
          padding: '20px 24px 24px 24px',
        }}
        onClose={onClose}
        open={open}
        testID={testID}
      >
        <Stack>
          <Text category="headline-small">{t($ => $.notificationBuilder.modal.setup.title)}</Text>
          <VSpacer size="7" />
          {isInternal ? (
            // Admin context - show retailer selector
            <>
              <Text category="body-large" sx={{ fontWeight: 500 }}>
                {t($ => $.notificationBuilder.modal.setup.selectRetailer)}
              </Text>
              <VSpacer size="4" />
              <Select
                disabled={isFetchingRetailers}
                error={cannotCreateScheduledNotification}
                label={
                  isFetchingRetailers
                    ? t($ => $.common.loading)
                    : t($ => $.notificationBuilder.modal.setup.selectRetailer)
                }
                onChangeValue={(value) => setRetailerId(value)}
                sx={{ width: '100%' }}
                testID={`${testID}-retailer-selector`}
                value={retailerId}
              >
                {retailers?.data?.map(({ id, name }) => (
                  <MenuItem
                    data-testid={`${testID}-retailer-selector-item-${id}`}
                    key={id}
                    value={id}
                  >
                    {name}
                  </MenuItem>
                ))}
              </Select>
              {!cannotCreateScheduledNotification
                && !!selectedRetailer
                && !hasExistingTwilioConfig && (
                <>
                  <VSpacer size="2" />
                  <Text category="body-small" color="info">
                    {t($ => $.notificationBuilder.modal.detail.warnings.usingDefaultTwilioAccount)}
                  </Text>
                </>
              )}
              {cannotCreateScheduledNotification && (
                <>
                  <VSpacer size="2" />
                  <Text category="body-small" color="error">
                    {t($ => $.notificationBuilder.modal.detail.errors.missingTwilioConfiguration)}
                  </Text>
                </>
              )}
              <VSpacer size="7" />
            </>
          ) : (
            <>
              <Text category="body-large" sx={{ fontWeight: 500 }}>
                {t($ => $.notificationBuilder.modal.setup.retailer)}
              </Text>
              <VSpacer size="4" />
              <Text category="body-medium" sx={{
                padding: '12px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}>
                {retailer?.name || t($ => $.notificationBuilder.modal.setup.loadingRetailer)}
              </Text>
              {cannotCreateScheduledNotification && (
                <>
                  <VSpacer size="2" />
                  <Text category="body-small" color="error">
                    {t(
                      $ => $.notificationBuilder.modal.detail.errors.retailerNeedsTwilioSubaccount,
                    )}
                  </Text>
                </>
              )}
              <VSpacer size="7" />
            </>
          )}
          <Text category="body-large" sx={{ fontWeight: 500 }}>
            {t($ => $.notificationBuilder.modal.setup.communicationType)}
          </Text>
          <VSpacer size="4" />
          <Select
            label={t($ => $.notificationBuilder.modal.setup.selectCommunicationType)}
            onChangeValue={(value) => setCommunicationType(value as ScheduledNotificationType)}
            sx={{ width: '100%' }}
            testID={`${testID}-communication-type`}
            value={communicationType}
          >
            {Object.values(ScheduledNotificationType).map((type) => (
              <MenuItem
                data-testid={`${testID}-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                key={type}
                value={type}
              >
                {communicationTypeLabel[type]}
              </MenuItem>
            ))}
          </Select>
          <VSpacer size="7" />
          <VSpacer size="7" />
          <Stack alignItems="center" direction="row" justifyContent="flex-end">
            <Button
              color="primary"
              onClick={onClose}
              testID={`${testID}-cancel-button`}
              variant="text"
            >
              {t($ => $.common.actions.cancel)}
            </Button>
            <HSpacer size="5" />
            <Button
              disabled={
                !retailerId.trim() ||
                !communicationType.trim() ||
                cannotCreateScheduledNotification
              }
              onClick={() => setShowDetailModal(true)}
              testID={`${testID}-continue-button`}
            >
              {t($ => $.common.actions.continue)}
            </Button>
          </Stack>
        </Stack>
      </Modal>

      {showDetailModal && (
        <NotificationDetailModal
          communicationType={communicationType as ScheduledNotificationType}
          onClose={onClose}
          onDraftSaved={onDraftSaved}
          open={showDetailModal}
          retailerId={retailerId}
          retailerName={retailer?.name || selectedRetailer?.name || ''}
          testID={`${testID}-detail`}
          title=""
        />
      )}
    </>
  );
};
